import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Alert, Badge, Button, Card, Checkbox, Field, Select, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { type Me, useAuth, useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { can, orgsWith } from '../../lib/permissions';
import { useDebounced } from '../../lib/useDebounced';
import {
  type Assignment,
  KEYS,
  type Organization,
  orgLabel,
  type Permission,
  type Role,
  type UserSummary,
  useOrganizations,
  usePermissions,
  useRoles,
} from './data';

/** Roles de cada usuario por organización: buscar, ver, asignar (con vencimiento opcional) y quitar. */
export function UserRolesPage() {
  const [text, setText] = useState('');
  const search = useDebounced(text.trim());
  const [user, setUser] = useState<UserSummary | null>(null);
  const orgs = useOrganizations();
  const users = useQuery({
    queryKey: KEYS.usuarios(search),
    queryFn: () => api<UserSummary[]>(`/api/acceso/usuarios${search ? `?buscar=${encodeURIComponent(search)}` : ''}`),
    placeholderData: (prev) => prev,
  });
  const orgName = (id: string) => {
    const o = orgs.data?.find((x) => x.id === id);
    return o ? orgLabel(o) : '—';
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-tinta">{t('acceso.usuarios_titulo')}</h1>
        <p className="max-w-2xl text-sm text-slate-600">{t('acceso.usuarios_descripcion')}</p>
      </div>
      {(users.error ?? orgs.error) && <Alert>{errorMessage(users.error ?? orgs.error)}</Alert>}

      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        <Card className="space-y-3 self-start">
          <Field id="buscar-usuario" type="search" label={t('acceso.buscar_usuario')} value={text} onChange={(e) => setText(e.target.value)} />
          {!users.data ? (
            !users.error && <Spinner label={t('comun.cargando')} />
          ) : users.data.length === 0 ? (
            <p className="text-sm text-slate-500">{t('acceso.sin_resultados')}</p>
          ) : (
            <ul aria-label={t('acceso.usuarios_titulo')} className="-mx-2">
              {users.data.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    aria-current={user?.id === u.id ? 'true' : undefined}
                    className={`w-full rounded px-2 py-2 text-left hover:bg-slate-50 ${user?.id === u.id ? 'bg-slate-100' : ''}`}
                    onClick={() => setUser(u)}
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-900">
                      {u.fullName}
                      {u.status !== 'activo' && <Badge tone="amber">{t('acceso.estado_inactivo')}</Badge>}
                    </span>
                    <span className="block truncate text-xs text-slate-500">
                      {u.email} · {orgName(u.organizationId)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {user && orgs.data ? (
          <UserAssignments key={user.id} user={user} orgs={orgs.data} orgName={orgName} />
        ) : (
          <p className="text-sm text-slate-600">{t('acceso.elegir_usuario')}</p>
        )}
      </div>
    </div>
  );
}

/** ¿Puede asignar este rol en esta organización? Igual que la API: debe tener todos sus permisos allí. */
function canGrantRole(me: Me, role: Role, organizationId: string, catalog: Permission[]): boolean {
  const mine = new Set(me.permissions[organizationId] ?? []);
  const needed = role.grantsAll ? catalog.map((p) => p.key) : role.permissions;
  return needed.every((k) => mine.has(k));
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-PA', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function UserAssignments({ user, orgs, orgName }: { user: UserSummary; orgs: Organization[]; orgName: (id: string) => string }) {
  const me = useMe();
  const { reloadMe } = useAuth();
  const qc = useQueryClient();
  const roles = useRoles();
  const catalog = usePermissions();
  const assignments = useQuery({
    queryKey: KEYS.asignaciones(user.id),
    queryFn: () => api<Assignment[]>(`/api/acceso/usuarios/${user.id}/roles`),
  });

  const assignableOrgs = orgs.filter((o) => orgsWith(me, 'plataforma.roles.asignar').includes(o.id));
  const [roleId, setRoleId] = useState('');
  // Varias organizaciones a la vez: el mismo rol se asigna en todas las marcadas.
  const [organizationIds, setOrganizationIds] = useState<string[]>(() => {
    const first = assignableOrgs.find((o) => o.id === user.organizationId)?.id ?? assignableOrgs[0]?.id;
    return first ? [first] : [];
  });
  const [expires, setExpires] = useState('');
  const [confirming, setConfirming] = useState<Assignment | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<string>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const message = await fn();
      await qc.invalidateQueries({ queryKey: KEYS.asignaciones(user.id) });
      if (user.id === me.id) await reloadMe();
      setNotice(message);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const assign = (e: FormEvent) => {
    e.preventDefault();
    return act(async () => {
      // El rol vale hasta el final del día elegido (hora local de quien asigna).
      const expiresAt = expires ? new Date(`${expires}T23:59:59`).toISOString() : undefined;
      const res = await api<{ created: number; skipped: string[] }>(`/api/acceso/usuarios/${user.id}/roles`, {
        json: { roleId, organizationIds, ...(expiresAt ? { expiresAt } : {}) },
      });
      setRoleId('');
      setExpires('');
      const done = organizationIds.length === 1 ? t('acceso.asignado') : t('acceso.asignado_varias', { n: res.created });
      return res.skipped?.length ? `${done} ${t('acceso.ya_tenia', { orgs: res.skipped.map(orgName).join(', ') })}` : done;
    });
  };

  const revoke = (a: Assignment) =>
    act(async () => {
      await api(`/api/acceso/asignaciones/${a.id}`, { method: 'DELETE' });
      setConfirming(null);
      return t('acceso.quitado');
    });

  const today = new Date();
  const minDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <div className="space-y-4">
      <Card title={t('acceso.roles_de', { nombre: user.fullName })}>
        <div className="space-y-3">
          {error && <Alert>{error}</Alert>}
          {notice && <Alert tone="success">{notice}</Alert>}
          {!assignments.data ? (
            assignments.error ? <Alert>{errorMessage(assignments.error)}</Alert> : <Spinner label={t('comun.cargando')} />
          ) : assignments.data.length === 0 ? (
            <p className="text-sm text-slate-500">{t('acceso.sin_roles')}</p>
          ) : (
            <ul className="divide-y divide-slate-100" aria-label={t('acceso.roles_de', { nombre: user.fullName })}>
              {assignments.data.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <div>
                    <span className="text-sm font-medium text-slate-900">{a.roleName}</span>
                    <span className="block text-xs text-slate-500">
                      {orgName(a.organizationId)}
                      {a.expiresAt && !a.expired && ` · ${t('acceso.vence', { fecha: formatDate(a.expiresAt) })}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.expired && <Badge tone="red">{t('acceso.vencido')}</Badge>}
                    {can(me, 'plataforma.roles.asignar', a.organizationId) && (
                      <Button variant="secondary" className="px-3 py-1" disabled={busy} onClick={() => setConfirming(a)}>
                        {t('acceso.quitar')}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {confirming && (
            <Alert tone="warning">
              <p>{t('acceso.quitar_confirmar', { rol: confirming.roleName, org: orgName(confirming.organizationId) })}</p>
              <div className="mt-2 flex gap-2">
                <Button variant="danger" disabled={busy} onClick={() => void revoke(confirming)}>
                  {t('acceso.si_quitar')}
                </Button>
                <Button variant="secondary" onClick={() => setConfirming(null)}>
                  {t('comun.cancelar')}
                </Button>
              </div>
            </Alert>
          )}
        </div>
      </Card>

      {assignableOrgs.length > 0 && roles.data && catalog.data && (
        <Card title={t('acceso.asignar_titulo')}>
          <form onSubmit={(e) => void assign(e)} className="space-y-3">
            <OrganizationPicker
              orgs={assignableOrgs}
              selected={organizationIds}
              onChange={(ids) => {
                setOrganizationIds(ids);
                setRoleId(''); // lo que se puede asignar depende de las organizaciones
              }}
            />
            <div className="max-w-md">
              <Select id="asignar-rol" label={t('acceso.rol')} value={roleId} onChange={(e) => setRoleId(e.target.value)} required>
                <option value="" disabled>
                  —
                </option>
                {roles.data.map((r) => {
                  // Debe poder asignarlo en todas las organizaciones marcadas.
                  const allowed = organizationIds.length > 0 && organizationIds.every((o) => canGrantRole(me, r, o, catalog.data));
                  return (
                    <option key={r.id} value={r.id} disabled={!allowed}>
                      {allowed ? r.name : `${r.name} (${t('acceso.no_puede_asignar')})`}
                    </option>
                  );
                })}
              </Select>
            </div>
            <div className="max-w-xs space-y-1">
              <Field id="asignar-vence" type="date" label={t('acceso.vencimiento')} min={minDate} value={expires} onChange={(e) => setExpires(e.target.value)} />
              <p className="text-xs text-slate-500">{t('acceso.vencimiento_ayuda')}</p>
            </div>
            <Button type="submit" disabled={busy || !roleId || organizationIds.length === 0}>
              {organizationIds.length > 1 ? t('acceso.asignar_en', { n: organizationIds.length }) : t('acceso.asignar')}
            </Button>
          </form>
        </Card>
      )}
    </div>
  );
}

/** Organizaciones donde asignar el rol, agrupadas; cada grupo se marca o desmarca entero. */
function OrganizationPicker({ orgs, selected, onChange }: { orgs: Organization[]; selected: string[]; onChange: (ids: string[]) => void }) {
  const groups = [...new Set(orgs.map((o) => o.groupName))];
  const toggle = (ids: string[], on: boolean) => onChange(on ? [...new Set([...selected, ...ids])] : selected.filter((id) => !ids.includes(id)));
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-slate-700">{t('acceso.organizaciones')}</legend>
      <p className="text-xs text-slate-500">{t('acceso.organizaciones_ayuda')}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {groups.map((g) => {
          const ids = orgs.filter((o) => o.groupName === g).map((o) => o.id);
          const all = ids.every((id) => selected.includes(id));
          return (
            <div key={g} className="rounded border border-slate-200 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-slate-800">{g}</span>
                {ids.length > 1 && (
                  <Button variant="link" className="text-xs" onClick={() => toggle(ids, !all)}>
                    {all ? t('acceso.ninguna') : t('acceso.todas')}
                  </Button>
                )}
              </div>
              <div className="space-y-1.5">
                {orgs
                  .filter((o) => o.groupName === g)
                  .map((o) => (
                    <Checkbox key={o.id} checked={selected.includes(o.id)} onChange={(e) => toggle([o.id], e.target.checked)} label={o.alias ? `${o.name} (${o.alias})` : o.name} />
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
